import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { omit, path } from 'ramda';
import type { Dispatch } from 'redux';
import debounce from 'lodash.debounce';

import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { DecisionHeader } from 'DecisionPackage/DecisionHeaderModule/components/DecisionHeader';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SignModal } from 'CorePackage/MainModule/components/SignModal';
import { SignatureImportModal } from 'KnmPackage/MainModule/components/SignatureImportModal';
import { decisionCheckSignCertificate, fetchDecisionSignUpload } from 'APIPackage/DecisionModule/services/decisionApi';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { adminGetFetchSettings } from 'PersonalAccountPackage/AdminModule/actions/adminAction';
import { FormLeavingGuard } from 'KnmPackage/MainModule/components/FormLeavingGuard';
import { getKnmModalIsVisible } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { AccessDeniedForTestModal } from 'CorePackage/MainModule/components/AccessDeniedForTestModal';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { getAutosaveSettings } from 'CorePackage/MainModule/common/getAutosaveSettings';
import { ErknmSignAfterCancelModal } from 'KnmPackage/MainModule/components/ErknmSignAfterCancelModal/ErknmSignAfterCancelModal';
import { setFieldSilent } from 'KnmPackage/MainModule/common/mutators';
import {
  flushSharedViolationsSync,
  sharedViolationsSyncDecorator,
} from 'KnmErknmPackage/ErknmViolationsModule/helpers/sharedViolationsSync/sharedViolationsSyncDecorator';
import { setValuesSilent } from 'KnmErknmPackage/ErknmViolationsModule/helpers/sharedViolationsSync/mutators';
import { useKnmFormSignHelpers } from 'KnmPackage/MainModule/hooks/useKnmFormSignHelpers';
import { createCheckSignCertificateHandler } from 'KnmPackage/MainModule/common/createCheckSignSertificateHandler';

import { IDecisionForm } from '../interfaces/IDecisionForm';
import styles from './DecisionForm.module.scss';
import { DecisionFormValidator } from '../types/DecisionFormValidator';
import { decisionFormValidator } from '../common/decisionFormValidator';
import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import { DecisionPassportFormRender } from './DecisionPassportFormRender';
import {
  decisionDraftSave,
  decisionSetFormRef,
  decisionSign,
  decisionSignSubmitFail,
} from '../actions/decisionActions';
import {
  getDecisionData,
  getDecisionFormRef,
  getDecisionLoading,
  getDecisionModalIsVisible,
  getKnmIsDecisionStatusFillInProcess,
  getKnmIsDecisionStatusReadyForApprove,
  getKnmIsDecisionStatusTypeCompleted,
  getKnmIsDecisionStatusTypeDisapproved,
} from '../selectors/decisionSelectors';
import { useDecisionFormErrorsInit } from '../hooks/useDecisionFormErrorsInit';
import { useDecisionFormRender } from '../hooks/useDecisionFormRender';
import { convertDecisionFormValuesToCreateParams } from '../common/convertDecisionFormValuesToCreateParams';
import { DecisionMassCancelHeader } from '../../DecisionHeaderModule/components/DecisionMassCancelHeader';
import { DecisionPrescribeCancelModal } from './DecisionPrescribeCancelModal/DecisionPrescribeCancelModal';
import { DecisionActCancelModal } from './DecisionActCancelModal/DecisionActCancelModal';
import { hasAnyCatalogFailed } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { HAS_GET_DATA_ERROR } from '../../../CorePackage/MainModule/constants/notices';
import { hasAnyDictionaryFailed } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { useDecisionFormStatusHintInit } from '../hooks/useDecisionsFormStatusHintInit';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { AccessRestrictionNoticeModal } from '../../../CorePackage/MainModule/components/AccessRestrictionNoticeModal';

export interface IDecisionFormProps {
  decisionData?: IDecision;
  handleSubmit: (values: IDecisionForm) => void;
  setCanSubmit: (canSubmit: boolean) => void;
}

export type IDecisionFormRenderProps = Omit<IDecisionFormProps, 'handleSubmit'> & FormRenderProps<IDecisionForm>;

interface IDecisionFormRenderStoreProps extends DecisionFormValidator {
  decisionFormRef: IDecisionFormRef | undefined;
  decisionRawData: IDecision | undefined;
  hasCatalogFailed: boolean;
  hasDictionaryFailed: boolean;
  isAccessDeniedForTestModalVisible: boolean;
  isActCancelVisible: boolean;
  isErknmSignAfterCancelVisible: boolean;
  isLoading: boolean;
  isPrescribeCancelVisible: boolean;
  isSignModalVisible: boolean;
  isSignUploadModalVisible: boolean;
  isAccessRestrictionNoticeModalVisible: boolean;
}

const DecisionFormRender = ({
  decisionData,
  errors,
  form,
  handleSubmit,
  hasValidationErrors,
  isNewDecision,
  isWatchOnly,
  settings,
  submitFailed,
  touched,
  values,
  visited,
  decisionFormRef,
  decisionRawData,
  hasCatalogFailed,
  hasDictionaryFailed,
  isAccessDeniedForTestModalVisible,
  isActCancelVisible,
  isErknmSignAfterCancelVisible,
  isLoading,
  isPrescribeCancelVisible,
  isSignModalVisible,
  isSignUploadModalVisible,
  isAccessRestrictionNoticeModalVisible,
}: IDecisionFormRenderProps & IDecisionFormRenderStoreProps) => {
  useDecisionFormStatusHintInit({ values, touched, settings });
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setFormRef = useCallback((formRef: IDecisionFormRef) => dispatch(decisionSetFormRef(formRef)), [dispatch]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const formRef: IDecisionFormRef = useRef(form);

  formRef.current = form;
  const passportData = useRef<IDecisionForm>();
  const isLoadingRef = useRef<boolean>();
  const saveDraftKnmRef = useRef<(data: IDecisionForm) => void>();
  const autosaveSettings = getAutosaveSettings(settings);
  const autosaveEnabledRef = useRef(autosaveSettings.enable);
  const autosaveMinsRef = useRef(autosaveSettings.mins);
  const debouncedOnFormValuesChangeRef = useRef<(dirty: boolean) => void>();

  const autosaveDelay = autosaveMinsRef.current * 60 * 1000;

  const hasAnyGetDataError = useMemo(() => {
    return hasCatalogFailed || hasDictionaryFailed;
  }, [hasCatalogFailed, hasDictionaryFailed]);

  useEffect(() => {
    setFormRef(formRef);
  }, [setFormRef, formRef]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (autosaveEnabledRef.current !== autosaveSettings.enable) {
      autosaveEnabledRef.current = autosaveSettings.enable;
    }
  }, [autosaveSettings.enable]);

  useEffect(() => {
    if (autosaveMinsRef.current !== autosaveSettings.mins) {
      autosaveMinsRef.current = autosaveSettings.mins;
    }
  }, [autosaveSettings.mins]);

  const saveDraftKnm = (data: IDecisionForm) => {
    if (decisionRawData && !hasAnyGetDataError) {
      const decisionRequestValues = convertDecisionFormValuesToCreateParams(data, decisionRawData, true);

      dispatch(decisionDraftSave(decisionRequestValues));
    }
  };

  useEffect(() => {
    saveDraftKnmRef.current = saveDraftKnm;
  }, [decisionRawData, hasAnyGetDataError, saveDraftKnm]);

  useEffect(() => {
    if (hasAnyGetDataError) {
      dispatch(
        addNoticeAction({
          renderContent: HAS_GET_DATA_ERROR,
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.HAS_GET_DATA_ERROR,
        }),
      );
    }
  }, [hasAnyGetDataError]);

  const compareDataAndSaveDraft = useCallback(() => {
    // Черновик должен уйти уже с перенесёнными между решениями правками нарушений
    flushSharedViolationsSync(formRef.current);

    const currentPassportData = { ...formRef.current?.getState()?.values };

    if (passportData.current) {
      const passportDataOld = { ...passportData.current };

      delete passportDataOld?.draft;
      delete passportDataOld?.draftSavedAt;
      delete currentPassportData?.draft;
      delete currentPassportData?.draftSavedAt;

      if (JSON.stringify(passportDataOld) !== JSON.stringify(currentPassportData)) {
        passportData.current = currentPassportData;
        saveDraftKnmRef.current && saveDraftKnmRef.current(currentPassportData);
      }
    }
  }, [formRef.current, autosaveMinsRef]);

  const onFormValuesChange = useCallback(
    (dirty: boolean) => {
      if (dirty && !isLoadingRef.current && autosaveEnabledRef.current) {
        compareDataAndSaveDraft();
      }
    },
    [autosaveEnabledRef, isLoadingRef],
  );

  useEffect(() => {
    if (autosaveDelay > 0) {
      const debouncedOnFormValuesChange = debounce(onFormValuesChange, autosaveDelay);

      debouncedOnFormValuesChangeRef.current = debouncedOnFormValuesChange;
    }
  }, [autosaveDelay]);

  // Подписка на состояния формы для отслеживания изменений и запуска таймера
  useEffect(() => {
    if (!formRef?.current) return;
    const unsubscribe = formRef.current.subscribe(
      ({ dirty }) => {
        if (debouncedOnFormValuesChangeRef.current) {
          debouncedOnFormValuesChangeRef.current(dirty);
        }
      },
      { values: true, dirty: true },
    );

    return () => {
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!decisionData?.id) {
      return;
    }

    if (!isLoadingRef.current) {
      passportData.current = { ...formRef.current?.getState()?.values };
    }
  }, [autosaveEnabledRef.current, isLoadingRef.current, decisionData?.id]);

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(e);

      hasValidationErrors && dispatch(decisionSignSubmitFail(errors));
    },
    [dispatch, handleSubmit],
  );

  const handleClearDraftPassportData = useCallback(() => {
    passportData.current = undefined;
  }, []);

  useDecisionFormErrorsInit({
    formRef,
    errors,
    submitFailed,
    touched,
    visited,
  });

  const {
    closeSignModal,
    closeSignUploadModal,
    handleSignUploadModalApply,
    handleClosePrescribeCancelModal,
    handleCloseActCancelModal,
    handleCloseSignAfterCancelModal,
  } = useDecisionFormRender({
    formRef,
    decisionRawData,
    handleClearDraftPassportData,
  });

  const { getSubjectInfo } = useKnmFormSignHelpers({});

  const isEnableKnoValidationSigning = useMemo(() => {
    const option = settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_KNO_VALIDATION_SIGNING);

    return option?.value === 'true';
  }, [settings]);

  const checkSignCertificate = useCallback(
    createCheckSignCertificateHandler(
      dispatch,
      decisionCheckSignCertificate,
      decisionData?.id,
      isEnableKnoValidationSigning,
    ),
    [dispatch, decisionData?.id, isEnableKnoValidationSigning],
  );

  const handleSign = useCallback(
    async (
      certificate: string,
      actGuid?: string,
      prescriptionGuid?: string,
      abortCheckCertificate?: () => void,
      isCancelSign?: boolean,
    ) => {
      if (decisionData?.id) {
        const subjectName = (await path(['SubjectName'], certificate)) as string;
        const subjectInfo = getSubjectInfo(subjectName);

        checkSignCertificate(subjectInfo, closeSignModal, () => {
          dispatch(decisionSign(certificate, decisionData.id, actGuid, prescriptionGuid, isCancelSign));
          closeSignModal();
        });
      }
    },
    [closeSignModal, dispatch, decisionData],
  );

  const handleSignAfterCancelModal = useCallback(
    async (certificate: string, prescriptionGuid?: string) => {
      dispatch(coreSetLoading(true));
      await handleSign(certificate, undefined, prescriptionGuid, () => {}, true).finally(() => {
        dispatch(coreSetLoading(false));
        handleCloseSignAfterCancelModal();
      });
    },
    [handleSign, handleCloseSignAfterCancelModal],
  );

  function renderForm(menu: ReactNode, content: ReactNode) {
    return (
      <>
        <form className={styles.Form} onSubmit={handleFormSubmit}>
          <DecisionHeader decisionData={decisionData} clearDraftPassportData={handleClearDraftPassportData} />
          <DecisionMassCancelHeader />
          {menu}
          <div className={styles.Body} ref={bodyRef}>
            {content}
          </div>
        </form>
        {!isWatchOnly && !isNewDecision && <FormLeavingGuard formRef={decisionFormRef} />}
        <SignModal
          visible={isSignModalVisible}
          title="Подписание Решения"
          onSign={handleSign}
          onClose={closeSignModal}
        />
        <SignatureImportModal
          visible={isSignUploadModalVisible}
          onSend={handleSignUploadModalApply}
          onClose={closeSignUploadModal}
          uploadFunc={fetchDecisionSignUpload}
          id={decisionData?.id}
          isEnableKnoValidationSigning={isEnableKnoValidationSigning}
        />
        <ErknmSignAfterCancelModal
          isDecision={true}
          isKnm={false}
          isPm={false}
          visible={isErknmSignAfterCancelVisible}
          onSign={handleSignAfterCancelModal}
          onClose={handleCloseSignAfterCancelModal}
        />
        <AccessRestrictionNoticeModal visible={isAccessRestrictionNoticeModalVisible} />
        <AccessDeniedForTestModal visible={isAccessDeniedForTestModalVisible} />
        <DecisionPrescribeCancelModal visible={isPrescribeCancelVisible} onClose={handleClosePrescribeCancelModal} />
        <DecisionActCancelModal visible={isActCancelVisible} onClose={handleCloseActCancelModal} />
      </>
    );
  }

  return (
    <DecisionPassportFormRender
      decisionData={decisionData}
      bodyRef={bodyRef}
      values={values}
      renderForm={renderForm}
      settings={settings}
    />
  );
};

// Массив должен быть постоянным
const FORM_DECORATORS = [sharedViolationsSyncDecorator];

const DecisionFormWithoutStore = ({ handleSubmit, ...rest }: IDecisionFormProps & IDecisionFormRenderStoreProps) => {
  function renderForm(props: FormRenderProps<IDecisionForm>) {
    return <DecisionFormRender {...rest} {...props} />;
  }

  const validatorObjSchema = new DecisionFormValidator();

  const validatorRest = omit(
    Object.keys(rest).filter(item => !(item in validatorObjSchema)),
    rest,
  ) as DecisionFormValidator;

  return (
    <Form<IDecisionForm>
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators, setFieldSilent, setValuesSilent }}
      decorators={FORM_DECORATORS}
      render={renderForm}
      validate={decisionFormValidator(validatorRest)}
    />
  );
};

const mapStateToProps = (state: IRootState): IDecisionFormRenderStoreProps => ({
  decisionData: getDecisionData(state),
  decisionFormRef: getDecisionFormRef(state),
  decisionRawData: getDecisionData(state),
  hasCatalogFailed: hasAnyCatalogFailed(state),
  hasDictionaryFailed: hasAnyDictionaryFailed(state),
  isAccessDeniedForTestModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_DENIED_FOR_TEST])(state),
  isActCancelVisible: getDecisionModalIsVisible([KnmModalsEnum.ERKNM_ACT_CANCEL])(state),
  isErknmSignAfterCancelVisible: getDecisionModalIsVisible([KnmModalsEnum.ERKNM_SIGN_AFTER_CANCEL])(state),
  isDecisionStatusCompleted: getKnmIsDecisionStatusTypeCompleted(state),
  isDecisionStatusDisapproved: getKnmIsDecisionStatusTypeDisapproved(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  isDecisionStatusReadyForApprove: getKnmIsDecisionStatusReadyForApprove(state),
  isEditableInStatusCommentsExist: true,
  isLoading: getDecisionLoading(state),
  isPrescribeCancelVisible: getDecisionModalIsVisible([KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL])(state),
  isSignModalVisible: getDecisionModalIsVisible([KnmModalsEnum.SIGN_MODAL])(state),
  isSignUploadModalVisible: getDecisionModalIsVisible([KnmModalsEnum.SIGN_UPLOAD_MODAL])(state),
  settings: getAdminFetchSettings(state),
  isAccessRestrictionNoticeModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_RESTRICTION_NOTICE])(state),
});

export const DecisionForm = connect(mapStateToProps)(DecisionFormWithoutStore);
